import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Users, Building, Badge } from 'lucide-react';
import { HexagonalLogo } from '../components/Logo';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    user_type: 'ffl_manufacturer'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const { login, error, clearError } = useAuth();

  const userTypes = [
    {
      id: 'leo_military',
      name: 'Law Enforcement & Military',
      description: 'High-security access with CJIS, FedRAMP, and military-grade compliance',
      icon: Badge,
      color: 'from-red-600 to-red-800'
    },
    {
      id: 'bail_bonds',
      name: 'Bail Bonds & Court System',
      description: 'Court integration with ECPA and privacy rights compliance',
      icon: Building,
      color: 'from-purple-600 to-purple-800'
    },
    {
      id: 'ffl_manufacturer',
      name: 'FFL & Manufacturer',
      description: 'Industry compliance with ATF and UL 2900 standards',
      icon: Users,
      color: 'from-blue-600 to-blue-800'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) clearError();
  };

  const handleUserTypeChange = (userType) => {
    setCredentials(prev => ({
      ...prev,
      user_type: userType
    }));
    if (error) clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();
    
    // If MFA is required and token is provided, include it in credentials
    const loginData = mfaRequired && mfaToken
      ? { ...credentials, mfa_token: mfaToken }
      : credentials;
    
    const result = await login(loginData);
    setIsLoading(false);
    
    if (!result.success) {
      // Check if MFA is required
      if (result.mfa_required) {
        setMfaRequired(true);
      }
    } else {
      // Login successful, reset MFA state
      setMfaRequired(false);
      setMfaToken('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl w-full space-y-8"
      >
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-8">
            <img 
              src="/FullLogo_Transparent_NoBuffer.png" 
              alt="Dark Matter Solutions" 
              className="h-24 w-auto object-contain"
              style={{ minHeight: '96px' }}
            />
          </div>
        </div>

        {/* User Type Selection */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-white text-center mb-6">
            Select Your User Type
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {userTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <motion.button
                  key={type.id}
                  type="button"
                  onClick={() => handleUserTypeChange(type.id)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    credentials.user_type === type.id
                      ? `border-blue-500 bg-blue-500/10`
                      : `border-gray-700 bg-gray-800/50 hover:border-gray-600`
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-center">
                    <div className={`w-12 h-12 bg-gradient-to-r ${type.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-medium text-white mb-2">{type.name}</h4>
                    <p className="text-xs text-gray-400">{type.description}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Login Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
        >
          <div className="space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                Username or email
              </label>
              <div className="mt-1 relative">
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={credentials.username}
                  onChange={handleInputChange}
                  className="input-field w-full pl-10"
                  placeholder="Enter your username or email"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={credentials.password}
                  onChange={handleInputChange}
                  className="input-field w-full pl-10 pr-10"
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* MFA Token Field - shown when MFA is required */}
            {mfaRequired && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4"
              >
                <label htmlFor="mfa_token" className="block text-sm font-medium text-gray-300">
                  MFA Code
                </label>
                <div className="mt-1 relative">
                  <input
                    id="mfa_token"
                    name="mfa_token"
                    type="text"
                    required={mfaRequired}
                    value={mfaToken}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaToken(value);
                      if (error) clearError();
                    }}
                    className="input-field w-full pl-10 text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    autoComplete="off"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  Enter the 6-digit code from your authenticator app
                </p>
              </motion.div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/20 rounded-lg p-3"
            >
              <p className="text-sm text-red-400">{error}</p>
            </motion.div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                `Sign in as ${userTypes.find(t => t.id === credentials.user_type)?.name.split(' ')[0]}`
              )}
            </button>
          </div>

          {/* Additional Info */}
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-4">
              This is a secure, compliance-focused platform for firearm management
            </p>
            <p className="text-sm text-gray-400">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-blue-400 hover:text-blue-300 transition-colors"
              >
                Create one here
              </Link>
            </p>
          </div>
        </motion.form>

        {/* Security Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-4 bg-gray-800/50 border border-gray-700 rounded-lg"
        >
          <div className="flex items-start">
            <Lock className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-white">Security Features</h3>
              <p className="text-xs text-gray-400 mt-1">
                • AES-256 encryption • Multi-factor authentication • Audit logging • 
                CJIS compliance • Real-time monitoring • Role-based access control
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
